/**
 * Dental Risk Report Constants and Static Configurations
 */

export const STORAGE_CACHE_KEY = 'DENTAL_RISK_REPORTS_CACHE_V1';

export const STATUS_CONFIGS = {
  pending: {
    label: 'รอตรวจสอบ',
    icon: '🟡',
    color: '#d97706',
    bg: '#fef3c7',
    border: '#fde68a',
    badgeClass: 'risk-status-badge--pending'
  },
  investigating: {
    label: 'กำลังสอบสวน RCA',
    icon: '🔵',
    color: '#2563eb',
    bg: '#dbeafe',
    border: '#bfdbfe',
    badgeClass: 'risk-status-badge--investigating'
  },
  resolved: {
    label: 'ดำเนินการแก้ไขแล้ว',
    icon: '🟢',
    color: '#16a34a',
    bg: '#dcfce7',
    border: '#bbf7d0',
    badgeClass: 'risk-status-badge--resolved'
  },
  closed: {
    label: 'ปิดเคสเรียบร้อย',
    icon: '⚪',
    color: '#475569',
    bg: '#f1f5f9',
    border: '#e2e8f0',
    badgeClass: 'risk-status-badge--closed'
  }
};

export const DOMAIN_OPTIONS = [
  { value: 'all', label: 'ทุกประเภทความเสี่ยง' },
  { value: 'clinical', label: '🩺 ทางคลินิก (Clinical)' },
  { value: 'cssd', label: '🧼 งานจ่ายกลางและฆ่าเชื้อ (CSSD)' },
  { value: 'non_clinical', label: '🏢 ทั่วไป/สิ่งแวดล้อม (Non-Clinical)' }
];

export const STATUS_OPTIONS = [
  { value: 'all', label: 'ทุกสถานะ' },
  { value: 'pending', label: '🟡 รอตรวจสอบ' },
  { value: 'investigating', label: '🔵 กำลังสอบสวน RCA' },
  { value: 'resolved', label: '🟢 แก้ไขแล้ว' },
  { value: 'closed', label: '⚪ ปิดเคส' }
];

export const SEVERITY_GROUP_OPTIONS = [
  { value: 'all', label: 'ทุกระดับความรุนแรง' },
  { value: 'sentinel', label: '🚨 Sentinel / วิกฤต (G-I / 4-5)' },
  { value: 'harm', label: '⚠️ Harm เกิดอันตราย (E-F / 3)' },
  { value: 'near_miss', label: '🛡️ Near Miss / No Harm (A-D / 1-2)' }
];

export const RCA_FISHBONE_CATEGORIES = [
  { id: 'man', label: 'บุคลากร (Man / Personnel)', icon: '👥' },
  { id: 'machine', label: 'เครื่องมือและอุปกรณ์ (Machine / Equipment)', icon: '⚙️' },
  { id: 'method', label: 'ขั้นตอนและวิธีปฏิบัติ (Method / Process)', icon: '📋' },
  { id: 'material', label: 'เวชภัณฑ์และวัสดุ (Material / Supplies)', icon: '📦' },
  { id: 'milieu', label: 'สภาพแวดล้อม (Milieu / Environment)', icon: '🏥' }
];

// Helper to determine if a severity level constitutes a sentinel event
export const isSentinelSeverity = (level) => {
  const lvl = String(level || '').toUpperCase().trim();
  return ['G', 'H', 'I', '4', '5'].includes(lvl);
};

// SQL Migration Script string สำหรับแสดงใน Modal ให้ผู้ใช้กดคัดลอก
export const DENTAL_RISK_SQL_SCRIPT = `-- 1. สร้างตาราง dental_risk_reports
CREATE TABLE IF NOT EXISTS public.dental_risk_reports (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  incident_number text UNIQUE NOT NULL,
  incident_date timestamptz DEFAULT timezone('utc'::text, now()) NOT NULL,
  location text NOT NULL,
  risk_domain text NOT NULL CHECK (risk_domain IN ('clinical', 'non_clinical', 'cssd')),
  category text NOT NULL,
  sub_category text,
  severity_level text NOT NULL,
  severity_score integer DEFAULT 1,
  title text NOT NULL,
  description text NOT NULL,
  immediate_action text,
  contributing_factors text,
  is_patient_involved boolean DEFAULT false,
  patient_hn text,
  procedure_name text,
  instrument_barcode text,
  sterile_cycle_id text,
  machine_id text,
  image_urls jsonb DEFAULT '[]'::jsonb,
  audio_note_url text,
  is_anonymous boolean DEFAULT false,
  reporter_name text,
  reporter_role text,
  contact_info text,
  status text DEFAULT 'pending' NOT NULL CHECK (status IN ('pending', 'investigating', 'resolved', 'closed')),
  investigator_name text,
  root_cause_analysis text,
  rca_fishbone_category text,
  corrective_action text,
  recall_batch_triggered boolean DEFAULT false,
  closed_at timestamptz,
  created_at timestamptz DEFAULT timezone('utc'::text, now()) NOT NULL,
  last_updated timestamptz DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 1.1 เพิ่มคอลัมน์ใหม่หากมีตารางเดิมอยู่แล้ว
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS instrument_barcode text;
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS sterile_cycle_id text;
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS machine_id text;
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS audio_note_url text;
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS rca_fishbone_category text;
ALTER TABLE public.dental_risk_reports ADD COLUMN IF NOT EXISTS recall_batch_triggered boolean DEFAULT false;

-- 2. สร้าง Indexes
CREATE INDEX IF NOT EXISTS idx_dental_risk_created_at ON public.dental_risk_reports(created_at DESC);
CREATE INDEX IF NOT EXISTS idx_dental_risk_status ON public.dental_risk_reports(status);
CREATE INDEX IF NOT EXISTS idx_dental_risk_severity ON public.dental_risk_reports(severity_level);
CREATE INDEX IF NOT EXISTS idx_dental_risk_location ON public.dental_risk_reports(location);
CREATE INDEX IF NOT EXISTS idx_dental_risk_barcode ON public.dental_risk_reports(instrument_barcode);
CREATE INDEX IF NOT EXISTS idx_dental_risk_cycle ON public.dental_risk_reports(sterile_cycle_id);

-- 3. ตั้งค่าความปลอดภัยและสิทธิ์การเข้าถึง (RLS)
ALTER TABLE public.dental_risk_reports ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS "Allow anon and authenticated insert" ON public.dental_risk_reports;
DROP POLICY IF EXISTS "Allow authenticated select" ON public.dental_risk_reports;
DROP POLICY IF EXISTS "Allow authenticated update" ON public.dental_risk_reports;

CREATE POLICY "Allow anon and authenticated insert"
ON public.dental_risk_reports FOR INSERT TO anon, authenticated WITH CHECK (true);

CREATE POLICY "Allow authenticated select"
ON public.dental_risk_reports FOR SELECT TO authenticated USING (true);

CREATE POLICY "Allow authenticated update"
ON public.dental_risk_reports FOR UPDATE TO authenticated USING (true) WITH CHECK (true);

GRANT ALL ON TABLE public.dental_risk_reports TO anon, authenticated, service_role;`;

// ข้อมูลตัวอย่างเริ่มต้น (Mock Initial Records) กรณีที่ยังไม่ได้รัน SQL ใน Supabase
export const SAMPLE_INITIAL_REPORTS = [
  {
    id: 'sample-001',
    incident_number: 'D-RSK-202609-001',
    incident_date: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
    location: 'คลีนิกห้อง 2',
    risk_domain: 'clinical',
    category: 'เครื่องมือและอุปกรณ์ทางการแพทย์',
    sub_category: 'หัวกรอ / ด้ามกรอแตกหักชำรุด',
    severity_level: 'E',
    severity_score: 5,
    title: 'หัวกรอ Diamond Bur หักขณะกรอฟันกรามล่าง',
    description: 'ขณะกรอเตรียมฟันกรามล่างซี่ 46 เพื่ออุดฟันด้วยคอมโพสิต หัวกรอไดมอนด์เกิดการหักครึ่งตัว ชิ้นส่วนหัวกรอตกลงบนลิ้นไก่และพื้นปาก แพทย์ใช้ Suction ดูดจับออกได้ทันที ตรวจเนื้อเยื่ออ่อนไม่พบบาดแผลฉีกขาด',
    immediate_action: 'หยุดการกรอฟันทันที นำชิ้นส่วนที่หักออกมาตรวจสอบความสมบูรณ์ ล้างทำความสะอาดช่องปาก และส่งถ่ายภาพรังสี Periapical ยืนยันว่าไม่มีเศษโลหะตกค้างในโพรงฟันหรือซอกเหงือก',
    contributing_factors: 'หัวกรอมีประวัติการใช้งานและผ่านการนึ่งฆ่าเชื้อซ้ำหลายรอบ สภาพก้านกรอมีรอยล้า (metal fatigue)',
    is_patient_involved: true,
    patient_hn: '69-001425',
    procedure_name: 'Composite Filling #46',
    image_urls: [],
    is_anonymous: false,
    reporter_name: 'ทพญ. นภัสสร สุขสวัสดิ์',
    reporter_role: 'ทันตแพทย์',
    contact_info: '081-445-9821',
    status: 'investigating',
    investigator_name: 'ทพ. นันต์ศักดิ์ (หัวหน้างานทันตกรรม)',
    root_cause_analysis: 'หัวกรอหมดอายุการใช้งานตามเกณฑ์รอบนึ่ง แต่ไม่มีระบบบันทึกคัดทิ้งหัวกรอที่เริ่มทื่อหรือมีรอยร้าว',
    rca_fishbone_category: 'machine',
    corrective_action: 'กำหนดเกณฑ์ตรวจสภาพหัวกรอทุกครั้งก่อนแพ็กซอง และจัดซื้อ Bur Block พร้อมระบบนับรอบการใช้งาน',
    created_at: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
    last_updated: new Date(Date.now() - 1 * 24 * 3600 * 1000).toISOString()
  },
  {
    id: 'sample-002',
    incident_number: 'D-RSK-202609-002',
    incident_date: new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString(),
    location: 'ห้อง sterile',
    risk_domain: 'clinical',
    category: 'การควบคุมการติดเชื้อและการสเตอไรล์ (Infection Control)',
    sub_category: 'หีบห่อบรรจุภัณฑ์ฉีกขาด / Seal เสื่อมสภาพ',
    severity_level: 'B',
    severity_score: 2,
    title: 'ตรวจพบซองสเตอไรล์ชุดขูดหินปูนฉีกขาดบริเวณรอยซีลขอบข้าง',
    description: 'เจ้าหน้าที่หน่วยจ่ายกลาง (CSSD) ขณะตรวจนับสต็อกเพื่อเตรียมจัดจ่ายไปยังห้องตรวจ พบซองบรรจุชุดขูดหินปูนมีรอยแยกขนาด 1 ซม. บริเวณขอบซีลด้านข้าง เกิดขึ้นก่อนนำส่งให้คลินิก ยังไม่มีผู้ป่วยได้รับผลกระทบ',
    immediate_action: 'อายัดชุดเครื่องมือทันที ไม่นำส่งห้องตรวจ นำส่งกลับห้องล้างเพื่อทำความสะอาดและแพ็กซองใหม่เข้าสู่รอบนึ่ง Autoclave อีกครั้ง',
    contributing_factors: 'เครื่องซีลซองปรับอุณหภูมิความร้อนไม่สม่ำเสมอ หรือแผ่นพลาสติกซองมีความหนาแน่นไม่เพียงพอต่อแรงกด',
    is_patient_involved: false,
    patient_hn: '',
    procedure_name: 'งานจ่ายกลาง / จัดเตรียมเครื่องมือ',
    image_urls: [],
    is_anonymous: false,
    reporter_name: 'นายนัฐพงษ์ รักษ์ดี',
    reporter_role: 'ผู้ช่วยทันตแพทย์ (CSSD)',
    contact_info: '089-771-2345',
    status: 'resolved',
    investigator_name: 'ทพ. นันต์ศักดิ์ (หัวหน้างานทันตกรรม)',
    root_cause_analysis: 'อุณหภูมิเครื่อง Rotary Heat Sealer ต่ำกว่ามาตรฐาน ทำให้การยึดติดของแถบกาวไม่สนิท',
    rca_fishbone_category: 'machine',
    corrective_action: 'บำรุงรักษาเครื่องซีลซอง วัดอุณหภูมิความร้อนทุกเช้าก่อนเริ่มทำงาน และทดสอบรอยซีลด้วย Seal Check Sheet ทุกสัปดาห์',
    created_at: new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString(),
    last_updated: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString()
  },
  {
    id: 'sample-003',
    incident_number: 'D-RSK-202609-003',
    incident_date: new Date(Date.now() - 1 * 24 * 3600 * 1000).toISOString(),
    location: 'คลีนิกห้อง 4',
    risk_domain: 'non_clinical',
    category: 'ความปลอดภัยและสิ่งแวดล้อม (Safety & Environment)',
    sub_category: 'อุบัติเหตุของมีคม / เข็มตำบุคลากร (Needle Stick Injury)',
    severity_level: '3',
    severity_score: 3,
    title: 'ผู้ช่วยทันตแพทย์ถูกเข็มฉีดยาชาทิ่มปลายนิ้วมือขณะสวมปลอกเข็ม',
    description: 'หลังเสร็จสิ้นหัตถการผ่าฟันคุด ผู้ช่วยทันตแพทย์ทำการเก็บโต๊ะเครื่องมือ และใช้มือทั้งสองข้างในการสวมปลอกเข็มฉีดยาชา (Two-handed recapping) เข็มแฉลบแทงทะลุถุงมือยางเข้าปลายนิ้วชี้มือซ้าย มีเลือดออกเล็กน้อย',
    immediate_action: 'ถอดถุงมือ บีบเลือดให้ไหลออก ล้างแผลด้วยน้ำไหลและฟอกสบู่เป็นเวลา 5 นาที ทาเบตาดีน รายงานหัวหน้างานทันที และเจาะเลือดผู้ป่วยกับบุคลากรเพื่อตรวจ HIV, HBV, HCV ตามแนวทาง Needle Stick Protocol',
    contributing_factors: 'รีบเร่งเคลียร์ห้องตรวจเนื่องจากมีเคสรอคิวถัดไป และละเลยการใช้เทคนิค One-handed scoop technique',
    is_patient_involved: false,
    patient_hn: '',
    procedure_name: 'ผ่าฟันคุด (Surgical Extraction #38)',
    image_urls: [],
    is_anonymous: true,
    reporter_name: '',
    reporter_role: 'ผู้ช่วยทันตแพทย์',
    contact_info: '',
    status: 'investigating',
    investigator_name: 'คณะกรรมการความปลอดภัยและอาชีวอนามัย',
    root_cause_analysis: 'ไม่ปฏิบัติตามมาตรฐาน Standard Precautions และไม่ได้ใช้อุปกรณ์ Safety Needle Recapper ที่มีประจำห้อง',
    rca_fishbone_category: 'method',
    corrective_action: 'จัดอบรมทบทวนแนวทางความปลอดภัยของมีคม บังคับใช้ One-handed scoop technique 100% และจัดหาอุปกรณ์ Cap-holder วางประจำทุกยูนิต',
    created_at: new Date(Date.now() - 1 * 24 * 3600 * 1000).toISOString(),
    last_updated: new Date(Date.now() - 6 * 3600 * 1000).toISOString()
  },
  {
    id: 'sample-004',
    incident_number: 'D-RSK-202609-004',
    incident_date: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
    location: 'คลีนิกห้อง 5',
    risk_domain: 'clinical',
    category: 'ภาวะแทรกซ้อนจากการรักษาทางทันตกรรม',
    sub_category: 'สิ่งแปลกปลอมหลุดลงคอ / สำลัก (Foreign Body Ingestion/Aspiration)',
    severity_level: 'F',
    severity_score: 6,
    title: 'ผู้ป่วยสำลักครอบฟันชั่วคราวหลุดลงคอขณะลองใส่ในช่องปาก',
    description: 'ขณะแพทย์กำลังลองใส่ครอบฟันชั่วคราว (Temporary Acrylic Crown) ที่ฟันกรามซี่ 36 ผู้ป่วยกลืนน้ำลายกะทันหัน ทำให้ชิ้นครอบฟันหลุดมือแพทย์ลงสู่คอหอย ผู้ป่วยมีอาการสำลัก ไอแรง แน่นหน้าอก และหายใจติดขัด',
    immediate_action: 'หยุดหัตถการทันที จัดท่าผู้ป่วยโน้มตัวไปข้างหน้าและเคาะหลัง (Back blows) ไม่พบชิ้นงานหลุดออกมา นำส่งห้องฉุกเฉิน (ER) ทันที ส่ง Chest X-Ray พบชิ้นงานตกในหลอดลมขวา แพทย์ ER ทำการ Bronchoscopy คีบชิ้นงานออกได้สำเร็จ ผู้ป่วยปลอดภัยและ Admit นอนสังเกตอาการ 1 คืน',
    contributing_factors: 'ไม่ได้ผูกไหมขัดฟัน (Dental floss tethering) ที่ตัวครอบฟันชั่วคราวขณะลองใส่ในปาก และไม่ได้กั้น Gauze throat pack',
    is_patient_involved: true,
    patient_hn: '68-009812',
    procedure_name: 'Temporary Crown Try-in #36',
    image_urls: [],
    is_anonymous: false,
    reporter_name: 'ทพ. อรรถพล รัตนกุล',
    reporter_role: 'ทันตแพทย์เฉพาะทาง',
    contact_info: '082-990-1123',
    status: 'pending',
    investigator_name: '',
    root_cause_analysis: '',
    rca_fishbone_category: '',
    corrective_action: '',
    created_at: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
    last_updated: new Date(Date.now() - 4 * 3600 * 1000).toISOString()
  }
];

export {
  INCIDENT_CRITERIA_PRESETS,
  COMPREHENSIVE_DENTAL_INCIDENTS,
  COMMON_INCIDENT_TAGS,
  getLocalDateTimeString
} from './constants';


